import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { List, Alert } from '@mui/material';
import { getVersion as getBookingsVersion } from '@eatclubapp/ec-bookings-library';
import { Box, Button } from '@eatclubapp/ec-component-library';
import { restaurantActivePropTypes } from '../../../data/models/Restaurant';
import { overrideDisabledRestaurants } from '../../../utils/flags';
import { getVersionText } from '../../../utils/helpers';
import useStyles, { navigationButtonStyles } from './NavigationDrawerStyles';
import { hasAccess } from '../../../utils/permissions';
import HideNavigationSVG from '../../../assets/hide_navigation.svg';
import RestaurantSwitcher from './RestaurantSwitcher/RestaurantSwitcher';
import { COLORS } from '../../../EatClubTheme';
import { userPropTypes } from '../../../data/models/User';
import Mixpanel from '../../../lib/mixpanel';
import { useHolidayHoursNavigationGuard } from '../../../context/HolidayHoursNavigationGuardContext';
import NavigationDrawerSkeleton from './NavigationDrawerSkeleton';

const COLUMN_LABELS = {
  management: 'MANAGEMENT',
  demand: 'DEMAND',
  settings: 'SETTINGS',
};

const DEFAULT_NAV_SECTION_META = {
  management: { isNew: true },
  demand: { isNew: false },
  settings: { isNew: false },
};

const NavigationDrawer = ({
  hideDrawer,
  navigationItems,
  allowHideNavigation,
  navSectionMeta = DEFAULT_NAV_SECTION_META,
  user,
  restaurantFetching,
  restaurantActive,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const navContainerRef = useRef(null);
  const holidayHoursNavGuard = useHolidayHoursNavigationGuard();
  const sectionMeta = { ...DEFAULT_NAV_SECTION_META, ...navSectionMeta };

  const getNavLink = (slug) => {
    if (slug === null || slug === undefined || slug === '') {
      return '/';
    }
    return `/${String(slug).toLowerCase().replace(/ /g, '-')}`;
  };

  const isStaff = user.userInfo?.userType === 'staff';
  // Do not block the whole nav on forecaster flag loading.
  const isLoading = (restaurantFetching || user?.userFetching) && !isStaff;

  const isLinkSelected = (item) => {
    if (item.activePathPrefixes?.length) {
      return item.activePathPrefixes.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
      );
    }
    const link = getNavLink(item.slug);
    if (link === '/') {
      return location.pathname === '/' || location.pathname === '/index.html';
    }
    return (
      link !== null && (location.pathname === link || location.pathname.startsWith(`${link}/`))
    );
  };

  const handleKeyDown = (e) => {
    if (!navContainerRef.current) {
      return;
    }

    const focusableElements = navContainerRef.current.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex="0"]',
    );
    const focusArray = Array.from(focusableElements);
    const currentIndex = focusArray.indexOf(document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = currentIndex < focusArray.length - 1 ? currentIndex + 1 : 0;
      focusArray[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusArray.length - 1;
      focusArray[prevIndex]?.focus();
    }
  };

  const renderColumnHeader = (column) => (
    <Box className={classes.sectionHeaderRow}>
      <Box className={classes.sectionHeaderLeft}>
        <span className={classes.sectionHeaderLabel}>{COLUMN_LABELS[column]}</span>
        {sectionMeta[column]?.isNew && <Box className={classes.newLabelSection}>NEW</Box>}
      </Box>
    </Box>
  );

  const renderNavRow = (item) => {
    const navLink = getNavLink(item.slug);
    const selected = isLinkSelected(item);
    const key = item.id;

    const handleClick = (e) => {
      if (holidayHoursNavGuard?.tryNavigateAway?.(e, navLink)) {
        return;
      }
      Mixpanel.track(`Nav link clicked - ${item.id}`, {
        page: item.id,
        origin: window.location.origin,
      });
      hideDrawer();
    };

    if (item.variant === 'create-offer') {
      return (
        <Box key={key} className={classes.createOfferWrap}>
          <NavLink
            to={navLink}
            onClick={handleClick}
            style={{ textDecoration: 'none' }}
            className={classes.createOfferNavLink}
          >
            <Button type='secondary' style={navigationButtonStyles.createOffer} newTab={false}>
              <span className={classes.createOfferButtonLabel}>{item.name}</span>
            </Button>
          </NavLink>
        </Box>
      );
    }

    return (
      <NavLink
        key={key}
        to={navLink}
        onClick={handleClick}
        className={`${classes.navRow} ${selected ? classes.navRowSelected : classes.navRowInactive}`}
      >
        <Box className={classes.navRowInner}>
          {item.icon && <Box className={classes.menuIcon}>{item.icon}</Box>}
          <Box className={classes.navRowLabel}>{item.name}</Box>
          {(item?.isBeta || item?.isNew) && (
            <Box className={classes.newLabel}>{item?.isBeta ? 'BETA' : 'NEW'}</Box>
          )}
        </Box>
      </NavLink>
    );
  };

  const mainNavItems = navigationItems.filter((nav) => nav.section === 1);

  const columnSections = [];
  if (!isLoading) {
    mainNavItems.forEach((nav) => {
      const noPermission =
        (nav?.allowedScopes && !hasAccess(user, nav?.allowedScopes)) ||
        (nav?.staffOnly && !isStaff);
      if (noPermission) {
        return;
      }
      const last = columnSections[columnSections.length - 1];
      if (!last || last.column !== nav.column) {
        columnSections.push({ column: nav.column, items: [nav] });
      } else {
        last.items.push(nav);
      }
    });
  }

  return (
    <Box className={classes.sidebar} ref={navContainerRef} onKeyDown={handleKeyDown}>
      <Box className={classes.navScrollOuter}>
        <Box className={classes.navScrollBody}>
          <Box className={classes.restaurantSwitcherWrap}>
            <RestaurantSwitcher hideDrawer={hideDrawer} />
          </Box>
          <Box className={classes.navTopDivider} role='separator' />
          {isStaff && (
            <Box style={{ padding: '0 20px' }}>
              <Box style={{ marginTop: '0', marginBottom: '8px' }}>
                <Alert
                  severity='info'
                  style={{ borderRadius: '20px', backgroundColor: '#E3EBF6' }}
                >{`Logged in as ${user.userInfo.name}`}</Alert>
              </Box>

              {restaurantActive?.restaurant?.objectId &&
                !restaurantActive?.restaurant?.enabled &&
                !overrideDisabledRestaurants.includes(restaurantActive?.restaurant?.objectId) && (
                  <Box style={{ marginTop: '8px' }}>
                    <Alert
                      severity='info'
                      style={{ borderRadius: '20px', backgroundColor: '#E3EBF6' }}
                    >
                      This restaurant is temporarily disabled
                    </Alert>
                  </Box>
                )}
            </Box>
          )}
          <Box style={{ position: 'relative' }}>
            {!isLoading && (
              <List className={classes.list} component='div' disablePadding>
                {columnSections.map((section, index) => (
                  <Box key={section.column} className={classes.navColumnBlock}>
                    {index > 0 && <Box className={classes.columnSectionBorder} />}
                    {renderColumnHeader(section.column)}
                    <Box className={classes.navSectionItems}>
                      {section.items.map((item) => renderNavRow(item))}
                    </Box>
                  </Box>
                ))}
              </List>
            )}

            {isLoading && (
              <List
                className={classes.list}
                component='div'
                disablePadding
                style={{
                  position: isLoading ? 'static' : 'absolute',
                  inset: isLoading ? 'auto' : 0,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <NavigationDrawerSkeleton dividerClassName={classes.columnSectionBorder} />
              </List>
            )}
          </Box>
        </Box>
        <Box className={classes.navScrollBottomFade} aria-hidden />
      </Box>
      <Box className={classes.footerBlock}>
        <Box className={classes.navTopDivider} role='separator' />

        <Box>
          <Box className={classes.versionNumber}>
            {getVersionText()}, {getBookingsVersion()}
          </Box>

          {allowHideNavigation && (
            <>
              <Box className={classes.navTopDivider} role='separator' />

              <Box style={{ marginTop: '0' }}>
                <Box
                  className={classes.navigationLink}
                  onClick={hideDrawer}
                  style={{ justifyContent: 'flex-start' }}
                >
                  <HideNavigationSVG style={{ color: COLORS.BLACK }} />
                  <Box
                    style={{
                      textTransform: 'uppercase',
                      letterSpacing: '1.2px',
                      lineHeight: '30px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Hide navigation
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

NavigationDrawer.propTypes = {
  hideDrawer: PropTypes.func.isRequired,
  allowHideNavigation: PropTypes.bool.isRequired,
  navSectionMeta: PropTypes.objectOf(PropTypes.shape({ isNew: PropTypes.bool })),
  restaurantFetching: PropTypes.bool.isRequired,
  navigationItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: userPropTypes.isRequired,
  restaurantActive: restaurantActivePropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
  restaurantFetching: state.restaurants.fetching,
  restaurantActive: state.restaurantActive,
});

export default connect(mapStateToProps, null)(NavigationDrawer);
